import { validateReviewerKey } from './transport';
import type { ResolvedOptions } from './types';
import { EMAIL_PATTERN } from './ui/dialog';

const PARAM_NAME = 'feedback';
const OFF_VALUE = 'off';

export interface GateResult {
  reviewerKey: string;
  /** true when mounted from a stored flag — caller should revalidate in the background */
  needsRevalidation: boolean;
  /** Project-configured default that pre-fills the dialog's email field */
  defaultEmail?: string;
}

interface StoredActivation {
  key: string;
  defaultEmail?: string;
}

function storageKey(token: string): string {
  return `bytax-feedback:${token}`;
}

function readStored(token: string): StoredActivation | null {
  try {
    const raw = localStorage.getItem(storageKey(token));
    if (!raw) return null;
    // Tolerant of the older stored shape (which also carried `activatedAt`):
    // only `key` is meaningful, extra fields are ignored rather than rejected.
    const parsed = JSON.parse(raw) as Partial<StoredActivation>;
    if (typeof parsed.key !== 'string' || parsed.key.length === 0) return null;
    // A tampered/corrupt stored email must not reach the dialog: drop it and
    // keep the key — the background revalidation refreshes the real value.
    const defaultEmail =
      typeof parsed.defaultEmail === 'string' && EMAIL_PATTERN.test(parsed.defaultEmail)
        ? parsed.defaultEmail
        : undefined;
    return { key: parsed.key, ...(defaultEmail ? { defaultEmail } : {}) };
  } catch {
    return null;
  }
}

export function storeActivation(token: string, key: string, defaultEmail?: string): void {
  try {
    // Always writes the full current shape, so a default the admin cleared is
    // also cleared from storage instead of lingering.
    localStorage.setItem(
      storageKey(token),
      JSON.stringify({ key, ...(defaultEmail ? { defaultEmail } : {}) }),
    );
  } catch {
    // Storage unavailable (private mode / quota): widget still works for this page view.
  }
}

export function clearActivation(token: string): void {
  try {
    localStorage.removeItem(storageKey(token));
  } catch {
    // ignore
  }
}

/**
 * Splits a fragment into the host's own prefix and the part that can hold
 * params. Hash-routing hosts put a route first (`#/orders?feedback=…`), so
 * params only start after the first `?`; without one the whole fragment is
 * treated as a query string (`#feedback=…`).
 */
function splitHash(hash: string): { prefix: string; query: string } {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const q = raw.indexOf('?');
  return q >= 0 ? { prefix: raw.slice(0, q), query: raw.slice(q + 1) } : { prefix: '', query: raw };
}

function readHashParam(): string | null {
  if (!location.hash) return null;
  return new URLSearchParams(splitHash(location.hash).query).get(PARAM_NAME);
}

/**
 * The key may arrive in the query string or in the fragment. The fragment is
 * what reviewer links use: it survives HTTP redirects on the host site (locale
 * prefix, auth gate, trailing slash) where a query string is dropped, and it is
 * never sent to the server, so the key stays out of access logs and Referer
 * headers. The query form stays supported for links already handed out.
 */
function readParam(): string | null {
  return new URLSearchParams(location.search).get(PARAM_NAME) ?? readHashParam();
}

function stripFromHash(url: URL): void {
  if (!url.hash) return;
  const { prefix, query } = splitHash(url.hash);
  const params = new URLSearchParams(query);
  if (!params.has(PARAM_NAME)) return;
  params.delete(PARAM_NAME);
  const rest = params.toString();
  // Only the widget's own key goes; the host's route and any other fragment
  // params are put back exactly as they came in.
  url.hash = rest ? `#${prefix}${prefix ? '?' : ''}${rest}` : prefix ? `#${prefix}` : '';
}

function stripParam(): void {
  try {
    const url = new URL(location.href);
    url.searchParams.delete(PARAM_NAME);
    stripFromHash(url);
    history.replaceState(history.state, '', url.toString());
  } catch {
    // ignore
  }
}

/**
 * Decides whether the widget mounts. The key is read from the query string or
 * the fragment (see readParam), so `?feedback=` and `#feedback=` below are
 * interchangeable. Resolution order:
 * 1. forceVisible (dev) → mount with placeholder key
 * 2. feedback=off → clear stored flag, don't mount
 * 3. feedback=<key> → validate against server; valid → store + mount
 * 4. stored flag → mount immediately, revalidate in background
 */
export async function checkGate(options: ResolvedOptions): Promise<GateResult | null> {
  if (options.forceVisible) {
    return { reviewerKey: 'rvw_dev', needsRevalidation: false };
  }

  const param = readParam();

  if (param === OFF_VALUE) {
    clearActivation(options.token);
    stripParam();
    return null;
  }

  if (param) {
    // Strip the key from the URL before the round-trip so it lingers in the
    // address bar / history for as little time as possible.
    stripParam();
    const result = await validateReviewerKey(options.endpoint, options.token, param);
    if (result.verdict === 'valid') {
      storeActivation(options.token, param, result.defaultReporterEmail);
      return {
        reviewerKey: param,
        needsRevalidation: false,
        ...(result.defaultReporterEmail ? { defaultEmail: result.defaultReporterEmail } : {}),
      };
    }
    return null;
  }

  const stored = readStored(options.token);
  if (!stored) return null;
  return {
    reviewerKey: stored.key,
    needsRevalidation: true,
    ...(stored.defaultEmail ? { defaultEmail: stored.defaultEmail } : {}),
  };
}
